// @ts-check
import { LitElement, html, css } from 'lit';
import { customElement} from 'lit/decorators.js';
export class VoyageToggleBox extends LitElement {
    constructor() {
        super();
        // The guards work similar to margin but for movement in the viewport
        // relative to this component's left, top, right and visible-top (from top).
        this.guardTop = 6;
        this.guardRight = 6;
        this.guardBottom = 6;
        this.guardLeft = 6;
        this.visibleTop = 100;

        // Default position
        this.xpos = 380;
        this.ypos = this.guardTop;
        this._width = 0;

        this.dragged = false;
        this.shown = false;
        this.transparent = false;

        this.startPos = {x:this.xpos,y:this.ypos,ox:this.xpos,oy:this.ypos};

        this.onResize = this.onResize.bind(this);

        this._afId = 0;
    }

    static get properties() {
        return {
            xpos: { type: Number },
            ypos: { type: Number },
            dragged: { type: Boolean, reflect: true },
            shown: { type: Boolean, reflect: true },
            transparent: { type: Boolean, reflect: true },
            guardTop: { type: Number, attribute: 'guard-top' },
            guardRight: { type: Number, attribute: 'guard-right' },
            guardBottom: { type: Number, attribute: 'guard-bottom' },
            guardLeft: { type: Number, attribute: 'guard-left' },
            visibleTop: { type: Number, attribute: 'visible-top' }
        };
    }

    updated(props) {
        props.forEach((oldValue, propName) => {
            if (propName.indexOf('guard') == 0 || propName === 'shown') {
                this.onResize();
            }
        });
    }

    static get styles() {
        return [
            css`
            :host {
                display: none;
                position: fixed;
                box-sizing: border-box;

                background: var(--neutrals-grey-02, black);
                border-radius: 0px;
                border: 1px solid var(--neutrals-grey-02, black);
                color: var(--neutrals-grey-10, white);
            }

            :host([shown]) {
                display: block;
            }

            :host([dragged]) {
                box-shadow: 5px 5px 10px rgba(0,0,0,0.5);
            }

            :host([transparent]) {
                opacity: 0.8;
            }

            :host(:hover) {
                opacity: 1;
            }

            #infobox {
                width: 100%;
                touch-action: none;
            }
        `];
    }

    render() {
        this.style.setProperty("top", `${this.ypos}px`);
        this.style.setProperty("left", `${this.xpos}px`);

        return html`
        <div id="infobox" @pointerup=${this.pup} @pointerdown=${this.pdown} @pointermove=${this.drag}><slot></slot></div>
        `;
    }

    pdown(evt) {
        const infobox = this.shadowRoot.querySelector('#infobox');
        this.startPos = {x:evt.pageX,y:evt.pageY,ox:this.xpos,oy:this.ypos};
        infobox.setPointerCapture(evt.pointerId);
        this.dragged = true;
        evt.preventDefault();
        return false;
    }

    pup(evt) {
        const infobox = this.shadowRoot.querySelector('#infobox');
        infobox.releasePointerCapture(evt.pointerId);
        this.dragged = false;
        evt.preventDefault();
        return false;
    }

    setGuardedPos(xpos, ypos) {
        const maxX = window.innerWidth - (this.guardRight + this._width);
        const maxY = window.innerHeight - (this.guardBottom + this.visibleTop);

        // Use guards
        if (xpos < this.guardLeft) {
            xpos = this.guardLeft;
        } else if (xpos > maxX) {
            xpos = maxX;
        }
        if (ypos < this.guardTop) {
            ypos = this.guardTop;
        } else if (ypos > maxY) {
            ypos = maxY;
        }

        this.xpos = xpos;
        this.ypos = ypos;
    }

    onResize() {
        if (this._afId !== 0) {
            return;
        }

        // Wait for the next frame, otherwise the size may not be ready yet
        this._afId = requestAnimationFrame( () => {
            this._width = this.getBoundingClientRect().width;
            this.setGuardedPos(this.xpos, this.ypos);
            this._afId = 0;
        });
    }

    drag(evt) {
        if (evt.pressure > 0.2) {
            let xpos = this.startPos.ox + evt.pageX - this.startPos.x;
            let ypos = this.startPos.oy + evt.pageY - this.startPos.y;
            

            this.setGuardedPos(xpos, ypos);

            evt.preventDefault();
            return false;
        }
    }

    firstUpdated() {
        const ro = new ResizeObserver( () => this.onResize() );

        ro.observe(this, { box: "border-box" });
        this.onResize();
    }

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('resize', this.onResize);
    }
}
customElements.define('voyage-toggle-box', VoyageToggleBox);

